// L'EFFACEUR — Panel admin Maintenance Mode toggle

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Power, AlertTriangle, RefreshCw } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../ui/button';

const MaintenanceTogglePanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ active: false, message: '', eta: '' });

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/maintenance/status`);
      setStatus(res.data);
      setForm({
        active: res.data.active || false,
        message: res.data.message || '',
        eta: res.data.eta || '',
      });
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const toggle = async (newActive) => {
    if (newActive && !form.message.trim()) {
      toast.error('Message requis pour activer le mode maintenance');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/maintenance/toggle`,
        { active: newActive, message: form.message, eta: form.eta },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newActive ? 'Mode maintenance ACTIVÉ' : 'Mode maintenance désactivé');
      setStatus({ active: newActive, message: form.message, eta: form.eta });
      setForm({ ...form, active: newActive });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500 font-mono text-sm">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 bg-orange-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(255,165,0,0.8)' }} />
          <h2 className="font-orbitron text-xl font-black text-white">Mode Maintenance</h2>
          <span className="font-mono text-xs text-orange-400/60">// SYSTÈME · CRITICAL</span>
        </div>
        <Button onClick={fetchStatus} variant="outline" className="text-xs">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`glass-card border p-6 ${status?.active ? 'border-red-400/40 bg-red-500/5' : 'border-success/40'}`}>
        <div className="flex items-start gap-4 mb-4">
          <Power className={`w-10 h-10 shrink-0 ${status?.active ? 'text-red-400' : 'text-success'}`} />
          <div>
            <h3 className="text-white font-bold text-lg mb-1">
              Statut actuel : {status?.active ? 'MAINTENANCE ACTIVE' : 'EN LIGNE'}
            </h3>
            <p className="text-gray-400 text-sm">
              {status?.active
                ? 'Le site affiche la page Maintenance à tous les visiteurs (sauf admins).'
                : 'Site accessible normalement à tous les utilisateurs.'}
            </p>
          </div>
        </div>
      </motion.div>

      <div className="glass-card border border-gray-800 p-6">
        <h3 className="text-white font-bold mb-4">Configuration</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-mono text-gray-400 uppercase mb-1">Message affiché aux utilisateurs</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Maintenance en cours pour mise à jour. Retour prévu sous peu."
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-cyan-400 focus:outline-none"
            />
            <p className="text-[10px] text-gray-500 mt-1">{form.message.length} / 500</p>
          </div>

          <div>
            <label className="block text-xs font-mono text-gray-400 uppercase mb-1">ETA (optionnel)</label>
            <input
              type="text"
              value={form.eta}
              onChange={(e) => setForm({ ...form, eta: e.target.value })}
              placeholder="ex: Retour prévu à 18h00 · 30 min · Demain matin"
              maxLength={120}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-cyan-400 focus:outline-none"
            />
          </div>

          {status?.active ? (
            <div className="border-t border-gray-800 pt-4">
              <Button
                onClick={() => toggle(false)}
                disabled={submitting}
                className="w-full bg-success text-black hover:bg-green-300 font-bold"
              >
                <Power className="w-4 h-4 mr-2" /> {submitting ? 'Désactivation...' : 'Désactiver le mode maintenance · Remettre en ligne'}
              </Button>
            </div>
          ) : (
            <div className="border-t border-gray-800 pt-4 space-y-2">
              <div className="flex items-start gap-2 p-3 rounded bg-orange-400/10 border border-orange-400/30">
                <AlertTriangle className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />
                <div className="text-xs text-orange-400">
                  Activer le mode maintenance déconnectera tous les utilisateurs non-admin.
                  Les paiements en cours échoueront. Utilise uniquement pour des urgences ou des fenêtres planifiées.
                </div>
              </div>
              <Button
                onClick={() => toggle(true)}
                disabled={submitting || !form.message.trim()}
                className="w-full bg-red-500 text-white hover:bg-red-600 font-bold"
              >
                <Power className="w-4 h-4 mr-2" /> {submitting ? 'Activation...' : 'ACTIVER le mode maintenance'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceTogglePanel;
